import { useState } from 'react';
import { Card } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PromissoryNote({ bankOffer, userData, onComplete }) {
  const [agreed, setAgreed] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleApprove = () => {
    setShowOtp(true);
  };

  const handleOtpSubmit = () => {
    if (otp.length === 6) {
      setIsLoading(true);
      setTimeout(() => {
        onComplete();
      }, 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-slate-50 to-slate-100">

      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg overflow-hidden bg-white">
            <img
              src="/images/logos/nafath.png"
              alt="نافذ"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">اصدار السند لأمر</h1>
          <p className="text-slate-500">عبر منصة نافذ - وزارة العدل</p>
        </div>

        <Card className="p-6 md:p-8 border-0 shadow-xl mb-6">
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm">
              <img
                src="/images/logos/nafath.png"
                alt="نافذ"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <p className="font-semibold text-slate-900">منصة نافذ</p>
              <p className="text-sm text-slate-500">وزارة العدل - المملكة العربية السعودية</p>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <h3 className="font-bold text-lg text-slate-900">تفاصيل السند لأمر</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">اسم المدين</p>
                <p className="font-semibold text-slate-900">{userData.name}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">رقم الهوية</p>
                <p className="font-semibold text-slate-900" dir="ltr">{userData.nationalId}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">المستفيد</p>
                <p className="font-semibold text-slate-900">{bankOffer.name}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">مدة السداد</p>
                <p className="font-semibold text-slate-900">{bankOffer.tenure} سنة</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
              <p className="text-sm text-emerald-700 mb-2">إجمالي مبلغ السند</p>
              <p className="text-3xl font-bold text-emerald-700">{formatCurrency(bankOffer.totalAmount)}</p>
            </div>

            <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                بالموافقة على هذا السند، فإنك تتعهد بسداد المبلغ المذكور أعلاه وفقاً لجدول السداد المتفق عليه مع البنك.
              </p>
            </div>
          </div>

          {!showOtp ? (
            <>
              <div className="flex items-start gap-3 mb-6">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  className="mt-1"
                />
                <label htmlFor="agree" className="text-sm text-slate-600 cursor-pointer">
                  أقر بأنني قرأت وفهمت شروط السند لأمر وأوافق على الالتزام بسداد المبلغ المحدد وفقاً للجدول الزمني المتفق عليه.
                </label>
              </div>

              <Button
                onClick={handleApprove}
                disabled={!agreed}
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium disabled:opacity-50"
              >
                الموافقة على أصدار السند
              </Button>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
                <p className="text-sm text-emerald-700 font-medium">
                  تم إرسال رمز التحقق إلى رقم الجوال المسجل في أبشر
                </p>
              </div>

              <div>
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="أدخل رمز التحقق"
                  className="h-14 text-lg text-center border-slate-200"
                  dir="ltr"
                />
              </div>

              <Button
                onClick={handleOtpSubmit}
                disabled={otp.length !== 6 || isLoading}
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'تأكيد إصدار السند'
                )}
              </Button>
            </motion.div>
          )}
        </Card>
      </div>
    </motion.div>
  );
}